import { type ComponentProps, type ComponentPropsWithoutRef, type FC, type ReactNode } from 'react';

import { Box, Grid, Image, PageGrid, Stack, Stretch } from '@eneco/sparky';
import { theme } from '@eneco/sparky/theme';
import type { BoxProps } from '@eneco/sparky/types';
import { getClassNames } from '@eneco/sparky/util/css';

import {
  contentContainerRecipe,
  type ContentContainerVariants,
  heroContainerRecipe,
  type HeroContainerVariants,
  imageContainerClass,
  rebrandedImageContainerClass,
  rightPlaceholderBoxRecipe,
  type RightPlaceholderBoxVariants,
} from './Hero.css';

export type { ContentContainerVariants, HeroContainerVariants, RightPlaceholderBoxVariants } from './Hero.css';

export const config = {
  minHeight: {
    initial: '280px',
    sm: '280px',
    md: '460px',
    lg: '640px',
  },
  offSetContentContainerBottom: theme.space[24],
};

export const HeroContainer: FC<ComponentProps<typeof Box> & HeroContainerVariants> = ({
  isVariableWidth,
  isMinMargin,
  className,
  ...rest
}) => <Box className={getClassNames(heroContainerRecipe({ isVariableWidth, isMinMargin }), className)} {...rest} />;

HeroContainer.displayName = 'HeroContainer';

export const ImageContainer: FC<ComponentPropsWithoutRef<'div'>> = ({ className, ...rest }) => (
  <div className={getClassNames(imageContainerClass, className)} {...rest} />
);

ImageContainer.displayName = 'ImageContainer';

export const RebrandedImageContainer: FC<ComponentPropsWithoutRef<'div'>> = ({ className, ...rest }) => (
  <div className={getClassNames(rebrandedImageContainerClass, className)} {...rest} />
);

interface HeroImageProps {
  src: string;
  alt?: string;
  sources?: ComponentProps<typeof Image>['sources'];
  overlay?: ReactNode;
}

export const HeroImage: FC<HeroImageProps> = ({ src, alt = '', sources, overlay }) => (
  <RebrandedImageContainer>
    <Stretch height={{ initial: false, lg: true }}>
      <Grid
        gridTemplateColumns="40px auto 1fr"
        gridTemplateRows={{ initial: '20px auto 1fr 40px', lg: '20px auto 1fr' }}>
        <Grid.Item gridColumn="1 / -1" gridRow="1 / -1">
          <Image
            alt={alt}
            height={{
              initial: config.minHeight.initial,
              sm: config.minHeight.sm,
              md: config.minHeight.md,
              lg: '100%',
            }}
            hasLazyLoad={false}
            fetchPriority="high"
            objectFit="cover"
            sources={sources}
            src={src}
            width="100%"
            borderRadius={{
              initial: ['square', 'square', 'square', 'square'],
              lg: ['rounded', 'rounded', 'rounded', 'rounded'],
            }}
          />
        </Grid.Item>
        {overlay && (
          <Grid.Item gridColumn="1 / -1" gridRow={{ initial: '1 / 4', lg: '1 / -1' }}>
            {overlay}
          </Grid.Item>
        )}
      </Grid>
    </Stretch>
  </RebrandedImageContainer>
);

HeroImage.displayName = 'HeroImage';

interface HeroContentGridProps {
  paddingTop?: BoxProps['paddingTop'];
  paddingBottom?: BoxProps['paddingBottom'];
  children: ReactNode;
}

/** Common grid layout for hero content sections */
export const HeroContentGrid: FC<HeroContentGridProps> = ({
  paddingTop = { initial: '0', lg: '16' },
  paddingBottom = { initial: '8', lg: '16' },
  children,
}) => (
  <Stack.Item>
    <PageGrid>
      <PageGrid.Item gridColumn="1/-1">
        <Box paddingTop={paddingTop} paddingBottom={paddingBottom}>
          <Grid gridTemplateColumns={{ initial: '1fr', lg: '1fr 1fr' }} columnGap={{ lg: '8', xl: '16' }}>
            {children}
          </Grid>
        </Box>
      </PageGrid.Item>
    </PageGrid>
  </Stack.Item>
);

HeroContentGrid.displayName = 'HeroContentGrid';

/** Left column slot in the hero content grid (order: mobile 2, desktop 1) */
export const HeroLeftColumn: FC<{ children: ReactNode }> = ({ children }) => (
  <Grid.Item order={{ initial: 2, lg: 1 }}>
    <Box paddingTop={{ initial: '2', lg: '0' }} paddingBottom={{ initial: '2', lg: '0' }}>
      {children}
    </Box>
  </Grid.Item>
);

HeroLeftColumn.displayName = 'HeroLeftColumn';

/** Right column slot in the hero content grid (order: mobile 1, desktop 2) */
export const HeroRightColumn: FC<{ children: ReactNode }> = ({ children }) => (
  <Grid.Item order={{ initial: 1, lg: 2 }}>{children}</Grid.Item>
);

HeroRightColumn.displayName = 'HeroRightColumn';

export const ContentContainer: FC<ComponentProps<typeof Stack> & ContentContainerVariants> = ({
  hasBottomPlaceholder,
  hasRightPlaceholder,
  isVariableHeight,
  className,
  ...rest
}) => (
  <Stack
    className={getClassNames(
      contentContainerRecipe({ hasBottomPlaceholder, hasRightPlaceholder, isVariableHeight }),
      className,
    )}
    {...rest}
  />
);

export const RightPlaceholderBox: FC<ComponentProps<typeof Box> & RightPlaceholderBoxVariants> = ({
  isVariableHeight,
  className,
  ...rest
}) => <Box className={getClassNames(rightPlaceholderBoxRecipe({ isVariableHeight }), className)} {...rest} />;
